import hash from 'object-hash';

const SALT = 'super-special-price-yanolja';

/**
 * 동일 입력 → 동일 출력 보장을 위한 메모이즈 캐시.
 *
 * object-hash가 일부 브라우저 번들에서 비결정적으로 동작하는 사례가 확인되었다:
 * `generateHashedId(undefined)`(예: `JSON.stringify(undefined)`가 string이 아닌 undefined를 반환하는 경우)
 * 호출 이후 내부 상태가 오염되어, 같은 입력이 호출 시점에 따라 다른 해시를 반환한다.
 * 이로 인해 URL 해시에 기록되는 모달 id 등 "입력 고정 = id 고정"을 전제로 하는 로직이 깨진다.
 * generateHashedId는 순수 함수이므로, (입력+길이)별 최초 결과를 캐싱해 결정성을 강제한다.
 */
const hashCache = new Map<string, string>();

/**
 * generateHashedId
 * - 입력 문자열을 고유한 해시 ID로 변환합니다.
 * - 동일한 입력 문자열은 항상 동일한 해시 값을 반환합니다. (캐시로 보장)
 * - 반환되는 해시의 길이를 조정할 수 있습니다.
 *
 * @param input - 해시로 변환할 입력 문자열
 * @param length - 반환할 해시 ID의 길이 (기본값: 6)
 * @returns - 변환된 고유 해시 ID
 */
export const generateHashedId = (input: string, length = 6): string => {
  const cacheKey = `${input}:${length}`;
  const cached = hashCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const fullHash = hash({ input, salt: SALT });
  const result = fullHash.slice(0, length);
  hashCache.set(cacheKey, result);
  return result;
};
